import { type UseQueryOptions } from '@tanstack/vue-query'
import { unref } from 'vue'

import { useContext } from '@/composables/context'
import { tripQueryKeys } from '@/composables/trip'
import { useQueryClient, useQuery, useMutation } from '@/composables/vue-query'
import { eventModule } from '@/store'
import {
  WaypointDayItem,
  WaypointDayItemCreate,
  WaypointDayItemUpdate,
  RearrangeWaypointDayItem,
  PlaceRecommendation,
  PointOfInterestPreview,
  PointOfInterest,
  DateRange,
} from '@/types/trip'
import { ActivityCreate, ActivityUpdate, Activity } from '@/types/activity'
import type { MaybeRef } from '@/types/types'
import { formattedDate } from '@/utils/date-utils'
import { LocationDataSource } from '@/utils/enums'
import { dataSourceToDataSourceSlug } from '@/utils/location'

export interface PointOfInterestPreviewQueryKeyParams {
  tripId: MaybeRef<string>
  dataSource: MaybeRef<LocationDataSource>
  externalId: MaybeRef<string>
}

export interface ActivityDetailQueryKeyParams {
  tripId: MaybeRef<string>
  activityId: MaybeRef<string>
}

export const activityKeys = {
  all: (tripId: MaybeRef<string>) =>
    [...tripQueryKeys.detail(tripId), 'activities'] as const,
  details: (tripId: MaybeRef<string>) =>
    [...activityKeys.all(tripId), 'detail'] as const,
  detail: (params: ActivityDetailQueryKeyParams) =>
    [...activityKeys.details(params.tripId), params.activityId] as const,
}

export interface WaypointDetailQueryKeyParams {
  tripId: MaybeRef<string>
  waypointId: MaybeRef<string>
}

export const placeQueryKeys = {
  all: (tripId: MaybeRef<string>) =>
    [...tripQueryKeys.detail(tripId), 'places'] as const,
  recommendations: (tripId: MaybeRef<string>) =>
    [...placeQueryKeys.all(tripId), 'recommendations'] as const,
  recommendationLists: (tripId: MaybeRef<string>) =>
    [...placeQueryKeys.recommendations(tripId), 'list'] as const,
  recommendationList: (params: WaypointDetailQueryKeyParams) =>
    [
      ...placeQueryKeys.recommendationLists(params.tripId),
      params.waypointId,
    ] as const,
}

export const pointOfInterestQueryKeys = {
  all: (tripId: MaybeRef<string>) =>
    [...tripQueryKeys.detail(tripId), 'pointsOfInterest'] as const,
  previews: (tripId: MaybeRef<string>) =>
    [...pointOfInterestQueryKeys.all(tripId), 'previews'] as const,
  preview: (params: PointOfInterestPreviewQueryKeyParams) =>
    [
      ...pointOfInterestQueryKeys.previews(params.tripId),
      params.dataSource,
      params.externalId,
    ] as const,
}

interface WaypointUpdate {
  date?: {
    startDate: DateRange
    endDate?: DateRange
  } | null
}

export function useUpdateWaypoint(
  tripId: MaybeRef<string>,
  waypointId: MaybeRef<string>,
) {
  const { $axios } = useContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: WaypointUpdate) => {
      await $axios.put(`/api/trips/${tripId}/waypoints/${waypointId}`, payload)
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: tripQueryKeys.itinerary(unref(tripId)),
        }),
        queryClient.invalidateQueries({
          queryKey: tripQueryKeys.summary(unref(tripId)),
        }),
      ])
    },
  })
}

export function useCreateWaypointDayItem(tripId: MaybeRef<string>) {
  const { $axios } = useContext()

  return useMutation({
    mutationFn: async (payload: {
      waypointId: string
      waypointDayId: string
      waypointDayDate: string
      newItem: WaypointDayItemCreate
    }) => {
      const {
        data: { item },
      } = await $axios.post<{ item: WaypointDayItem }>(
        `/api/trips/${unref(tripId)}/waypoints/${payload.waypointId}/days/${payload.waypointDayId}/item`,
        payload.newItem,
      )
      return item
    },
    onSuccess: (_data, variables, _context) => {
      eventModule.setValues({ date: formattedDate(variables.waypointDayDate) })
      eventModule.newMessage('addedToDate')
    },
    onError: () => {
      eventModule.newError('error.waypoint.dailyPlan.create')
    },
  })
}

export function useUpdateWaypointDayItem(tripId: string, waypointId: string) {
  const { $axios } = useContext()

  return useMutation({
    mutationFn: async (payload: {
      waypointDayId: string
      itemId: string
      updateItem: WaypointDayItemUpdate
    }) => {
      if (payload.updateItem.endTime && !payload.updateItem.startTime) {
        payload.updateItem.startTime = payload.updateItem.endTime
        payload.updateItem.endTime = null
      }

      await $axios.put(
        `/api/trips/${tripId}/waypoints/${waypointId}/days/${payload.waypointDayId}/item/${payload.itemId}`,
        payload.updateItem,
      )
    },
    onSuccess: () => {
      eventModule.newMessage('success.waypoint.dailyPlan.update')
    },
    onError: () => {
      eventModule.newError('error.waypoint.dailyPlan.update')
    },
  })
}

export function useDeleteWaypointDayItem(tripId: string, waypointId: string) {
  const { $axios } = useContext()

  return useMutation({
    mutationFn: async (payload: { waypointDayId: string; itemId: string }) => {
      await $axios.delete(
        `/api/trips/${tripId}/waypoints/${waypointId}/days/${payload.waypointDayId}/item/${payload.itemId}`,
      )
    },
    onSuccess: () => {
      eventModule.newMessage('success.waypoint.dailyPlan.delete')
    },
    onError: () => {
      eventModule.newError('error.waypoint.dailyPlan.delete')
    },
  })
}

export function useRearrangeWaypointDayItem(tripId: MaybeRef<string>) {
  const { $axios } = useContext()

  return useMutation({
    mutationFn: async (variables: RearrangeWaypointDayItem) => {
      const { waypointId, ...payload } = variables
      await $axios.put(
        `/api/trips/${unref(tripId)}/waypoints/${waypointId}/days/rearrange`,
        payload,
      )
    },
    onSuccess: () => {
      eventModule.newMessage('success.waypoint.dailyPlan.rearrange')
    },
    onError: () => {
      eventModule.newError('error.waypoint.dailyPlan.rearrange')
    },
  })
}

export function useActivity(
  tripId: string,
  waypointId: string,
  activityId: string,
) {
  const { $axios } = useContext()

  return useQuery({
    queryKey: activityKeys.detail({ tripId, activityId }),
    queryFn: async () => {
      const {
        data: { data },
      } = await $axios.get<{ data: Activity }>(
        `/api/trips/${tripId}/waypoints/${waypointId}/activities/${activityId}`,
      )
      return data
    },
  })
}

export function useCreateActivity(tripId: string, waypointId: string) {
  const { $axios } = useContext()

  return useMutation({
    mutationFn: async (newItem: ActivityCreate) => {
      const {
        data: { data },
      } = await $axios.post<{ data: Activity }>(
        `/api/trips/${tripId}/waypoints/${waypointId}/activities/`,
        newItem,
      )
      return data
    },
    onSuccess: () => {
      eventModule.newMessage('success.activity.create')
    },
    onError: () => {
      eventModule.newError('error.activity.create')
    },
  })
}

export function useUpdateActivity(tripId: string, waypointId: string) {
  const { $axios } = useContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (activity: ActivityUpdate) => {
      await $axios.put(
        `/api/trips/${tripId}/waypoints/${waypointId}/activities/${activity._id}/`,
        activity,
      )
    },
    onSuccess: async (_data, activity, _context) => {
      if (activity._id) {
        await queryClient.invalidateQueries({
          queryKey: activityKeys.detail({ tripId, activityId: activity._id }),
        })
      }

      eventModule.newMessage('success.activity.update')
    },
    onError: () => {
      eventModule.newError('error.activity.update')
    },
  })
}

export function useDeleteActivity(tripId: string) {
  const { $axios } = useContext()

  return useMutation({
    mutationFn: async (payload: { waypointId: string; activityId: string }) => {
      await $axios.delete(
        `/api/trips/${tripId}/waypoints/${payload.waypointId}/activities/${payload.activityId}`,
      )
    },
    onSuccess: () => {
      eventModule.newMessage('success.activity.delete')
    },
    onError: () => {
      eventModule.newError('error.activity.delete')
    },
  })
}

export function usePlaceRecommendations(tripId: string, waypointId: string) {
  const { $axios } = useContext()

  return useQuery({
    queryKey: placeQueryKeys.recommendationList({ tripId, waypointId }),
    queryFn: async () => {
      const {
        data: { places },
      } = await $axios.get<{ places: PlaceRecommendation[] }>(
        `/api/trips/${tripId}/waypoints/${waypointId}/recommendations`,
      )

      return places
    },
  })
}

export function useAddPointOfInterestToWaypoint(
  tripId: string,
  waypointId: string,
) {
  const { $axios } = useContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (pointOfInterest: PointOfInterest) => {
      await $axios.post(
        `/api/trips/${tripId}/waypoints/${waypointId}/pointOfInterests`,
        { pointOfInterest },
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: placeQueryKeys.recommendationList({ tripId, waypointId }),
      })
      eventModule.newMessage('success.poi.create')
    },
    onError: () => {
      eventModule.newError('error.poi.create')
    },
  })
}

export interface UsePointOfInterestPreviewOptions {
  tripId: string
  waypointId: string
  dataSource: LocationDataSource
  externalId: string
  queryOptions?: UseQueryOptions<PointOfInterest>
}

export function usePointOfInterestPreview(
  options: UsePointOfInterestPreviewOptions,
) {
  const { tripId, waypointId, dataSource, externalId, queryOptions } = options
  const { $axios } = useContext()

  const dataSourceSlug = dataSourceToDataSourceSlug(dataSource)

  if (!dataSourceSlug) {
    throw new Error(`Invalid dataSource: ${dataSource}`)
  }
  return useQuery({
    queryKey: pointOfInterestQueryKeys.preview({
      tripId,
      dataSource,
      externalId,
    }),
    queryFn: async () => {
      const {
        data: { place },
      } = await $axios.get<{ place: PointOfInterestPreview }>(
        `/api/trips/${tripId}/waypoints/${waypointId}/pointOfInterests/preview/${dataSourceSlug}/${externalId}`,
      )

      const pointOfInterestPreview: PointOfInterest = {
        name: place.name,
        metadata: place,
      }
      return pointOfInterestPreview
    },
    ...queryOptions,
  })
}

export function useDeletePointOfInterest() {
  const { $axios } = useContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (variables: {
      tripId: string
      waypointId: string
      poiId: string
    }) => {
      const { tripId, waypointId, poiId } = variables
      await $axios.delete(
        `/api/trips/${tripId}/waypoints/${waypointId}/pointOfInterests/${poiId}`,
      )
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: placeQueryKeys.recommendationList({
          tripId: variables.tripId,
          waypointId: variables.waypointId,
        }),
      })
      eventModule.newMessage('success.poi.delete')
    },
    onError: () => {
      eventModule.newError('error.poi.delete')
    },
  })
}
